// src/views/PersonalView.tsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { StreakCounter } from '@/components/dashboard/StreakCounter';
import { ProgressTracker } from '@/components/achievements/ProgressTracker';
import { AchievementGrid } from '@/components/achievements/AchievementGrid';
import { HeatMap } from '@/components/analytics/HeatMap';
import { useAuth } from '@/contexts/AuthContext';
import { useTestingData } from '@/hooks/useTestingData';
import { getUserTestingHistory } from '@/lib/firestore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { User } from 'lucide-react';

export const PersonalView: React.FC = () => {
  const { user } = useAuth();
  const { stats } = useTestingData(user?.uid);
  const [heatmapData, setHeatmapData] = useState<Array<{ date: string; count: number }>>([]);
  const [loadingHeatmap, setLoadingHeatmap] = useState(true);

  const loadHeatmap = async () => {
    if (user?.uid) {
      setLoadingHeatmap(true);
      try {
        const data = await getUserTestingHistory(user.uid, 84);
        setHeatmapData(data);
        console.log('✅ Personal heatmap data loaded:', data.filter(d => d.count > 0).length, 'days with activity');
      } catch (error) {
        console.error('❌ Error loading personal heatmap:', error);
        // Fallback to empty data
        setHeatmapData(Array.from({ length: 84 }, (_, i) => {
          const date = new Date();
          date.setDate(date.getDate() - i);
          return {
            date: date.toISOString().split('T')[0],
            count: 0,
          };
        }));
      } finally {
        setLoadingHeatmap(false);
      }
    }
  };

  useEffect(() => {
    loadHeatmap();
  }, [user?.uid]);
  
  // Refresh heatmap when stats change (e.g., after recording a test)
  useEffect(() => {
    if (stats?.totalDays) {
      loadHeatmap();
    }
  }, [stats?.totalDays]);

  if (!stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-pulse">📱</div>
          <p className="text-muted-foreground">Loading your progress...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      <div>
        <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
          <User className="w-10 h-10" />
          My Progress
        </h1>
        <p className="text-muted-foreground">
          Track your personal testing journey and achievements
        </p>
      </div>

      <StreakCounter
        currentStreak={stats.currentStreak}
        longestStreak={stats.longestStreak}
        totalDays={stats.totalDays}
      />

      <div className="grid md:grid-cols-2 gap-6">
        <ProgressTracker userStats={stats} />
        <Card>
          <CardHeader>
            <CardTitle>Personal Stats</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-muted">
                <div className="text-sm text-muted-foreground mb-1">This Week</div>
                <div className="text-3xl font-bold text-blue-500">
                  {stats.weeklyTests}
                </div>
              </div>
              <div className="p-4 rounded-lg bg-muted">
                <div className="text-sm text-muted-foreground mb-1">This Month</div>
                <div className="text-3xl font-bold text-purple-500">
                  {stats.monthlyTests}
                </div>
              </div>
              <div className="p-4 rounded-lg bg-muted">
                <div className="text-sm text-muted-foreground mb-1">Best Streak</div>
                <div className="text-3xl font-bold text-yellow-500">
                  {stats.longestStreak}
                </div>
              </div>
              <div className="p-4 rounded-lg bg-muted">
                <div className="text-sm text-muted-foreground mb-1">Total Days</div>
                <div className="text-3xl font-bold text-green-500">
                  {stats.totalDays}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {loadingHeatmap ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              📅 Activity Heatmap
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center h-48">
              <p className="text-muted-foreground">Loading activity data...</p>
            </div>
          </CardContent>
        </Card>
      ) : heatmapData.every(d => d.count === 0) ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              📅 Activity Heatmap
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center h-48 space-y-3">
              <div className="text-6xl">📊</div>
              <p className="text-muted-foreground text-center">
                No testing activity yet
              </p>
              <p className="text-sm text-muted-foreground/70">
                Start testing to see your activity pattern!
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <HeatMap data={heatmapData} />
      )}

      <div>
        <h2 className="text-2xl font-bold mb-4">My Achievements</h2>
        <AchievementGrid userStats={stats} />
      </div>
    </motion.div>
  );
};

